import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
const COLORS = ['#A855F7', '#3B82F6', '#22D3EE', '#EC4899', '#F5F5F5'];
type Burst = {
  id: number;
  angle: number;
  distance: number;
  size: number;
  color: string;
  delay: number;
  duration: number;
};
export function IntroAnimation() {
  const [phase, setPhase] = useState<'black' | 'explode' | 'reveal' | 'done'>('black');
  // Pre-compute particles so they're stable
  const bursts = useMemo<Burst[]>(() => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
    const count = isMobile ? 60 : 110;
    return Array.from({
      length: count
    }).map((_, i) => ({
      id: i,
      angle: Math.random() * Math.PI * 2,
      distance: 200 + Math.random() * (isMobile ? 500 : 900),
      size: 4 + Math.random() * (isMobile ? 14 : 22),
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      delay: Math.random() * 0.15,
      duration: 0.9 + Math.random() * 0.7
    }));
  }, []);
  useEffect(() => {
    // Lock body scroll while intro is playing
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const t1 = setTimeout(() => setPhase('explode'), 400);
    const t2 = setTimeout(() => setPhase('reveal'), 700);
    const t3 = setTimeout(() => setPhase('done'), 2200);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      document.body.style.overflow = original;
    };
  }, []);
  // Unlock scroll when reveal phase starts (so the page is interactive ASAP)
  useEffect(() => {
    if (phase === 'reveal') {
      document.body.style.overflow = '';
    }
  }, [phase]);
  if (phase === 'done') return null;
  return <AnimatePresence data-id="element-292">
      <motion.div key="intro" initial={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[100] pointer-events-none overflow-hidden" aria-hidden="true" data-id="element-293">
        {/* Black curtain that fades away on reveal */}
        <motion.div className="absolute inset-0 bg-background" initial={{
        opacity: 1
      }} animate={{
        opacity: phase === 'reveal' ? 0 : 1
      }} transition={{
        duration: 1.1,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-294" />

        {/* Pre-explosion radial glow charge-up */}
        <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full" initial={{
        width: 0,
        height: 0,
        opacity: 0
      }} animate={phase === 'black' ? {
        width: 0,
        height: 0,
        opacity: 0
      } : phase === 'explode' ? {
        width: 600,
        height: 600,
        opacity: 1
      } : {
        width: 1200,
        height: 1200,
        opacity: 0
      }} transition={{
        duration: phase === 'explode' ? 0.35 : 0.9,
        ease: 'easeOut'
      }} style={{
        background: 'radial-gradient(circle, rgba(255,255,255,0.6), rgba(168,85,247,0.4) 30%, rgba(34,211,238,0.3) 50%, transparent 70%)',
        filter: 'blur(40px)'
      }} data-id="element-295" />

        {/* Bright white core flash */}
        <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 rounded-full bg-white" initial={{
        scale: 0,
        opacity: 0
      }} animate={phase === 'explode' ? {
        scale: [0, 1.6, 0.4],
        opacity: [0, 1, 0]
      } : {
        scale: 0,
        opacity: 0
      }} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} style={{
        boxShadow: '0 0 80px 40px rgba(255,255,255,0.7), 0 0 160px 80px rgba(168,85,247,0.5)'
      }} data-id="element-296" />

        {/* The explosion of brand-color particles */}
        {(phase === 'explode' || phase === 'reveal') && <div className="absolute top-1/2 left-1/2 w-0 h-0" data-id="element-297">
            {bursts.map(b => {
          const tx = Math.cos(b.angle) * b.distance;
          const ty = Math.sin(b.angle) * b.distance;
          return <motion.div key={b.id} initial={{
            x: 0,
            y: 0,
            opacity: 1,
            scale: 0.3
          }} animate={{
            x: tx,
            y: ty,
            opacity: 0,
            scale: 1.2
          }} transition={{
            duration: b.duration,
            delay: b.delay,
            ease: [0.16, 1, 0.3, 1]
          }} className="absolute rounded-full" style={{
            width: b.size,
            height: b.size,
            marginLeft: -b.size / 2,
            marginTop: -b.size / 2,
            background: b.color,
            boxShadow: `0 0 ${b.size * 2}px ${b.color}, 0 0 ${b.size * 4}px ${b.color}80`
          }} data-id="element-298" />;
        })}
          </div>}

        {/* Subtle screen flash */}
        <motion.div className="absolute inset-0 bg-white pointer-events-none" initial={{
        opacity: 0
      }} animate={phase === 'explode' ? {
        opacity: [0, 0.25, 0]
      } : {
        opacity: 0
      }} transition={{
        duration: 0.4,
        ease: 'easeOut'
      }} data-id="element-299" />
      </motion.div>
    </AnimatePresence>;
}